import { Card, CardContent, Typography, Box } from '@mui/material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  LineChart,
  Line,
  Legend,
} from 'recharts'
import { MonthlyNettoData, SupplierNettoData } from '@/types'
import { formatCurrency } from '@/utils/formatters'

interface MonthlyNettoChartProps {
  data: MonthlyNettoData[]
  isLoading?: boolean
}

const COLORS = [
  '#1976d2',
  '#dc004e',
  '#ff9800',
  '#4caf50',
  '#9c27b0',
  '#00bcd4',
  '#ff5722',
  '#795548',
  '#607d8b',
  '#3f51b5',
]

export function MonthlyNettoChart({ data, isLoading }: MonthlyNettoChartProps) {
  if (isLoading) {
    return (
      <Card sx={{ height: 350 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Monthly NETTO
          </Typography>
          <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="textSecondary">Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (data.length === 0) {
    return (
      <Card sx={{ height: 350 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Monthly NETTO
          </Typography>
          <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="textSecondary">No data available</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card sx={{ height: 350 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Monthly NETTO
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`} />
            <Tooltip
              formatter={(value: number) => [formatCurrency(value), 'NETTO']}
              contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0' }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="netto"
              stroke="#1976d2"
              strokeWidth={2}
              dot={{ fill: '#1976d2', r: 4 }}
              activeDot={{ r: 6 }}
              name="NETTO"
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

interface TopSuppliersChartProps {
  data: SupplierNettoData[]
  isLoading?: boolean
}

export function TopSuppliersChart({ data, isLoading }: TopSuppliersChartProps) {
  if (isLoading) {
    return (
      <Card sx={{ height: 350 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top 10 Suppliers
          </Typography>
          <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="textSecondary">Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (data.length === 0) {
    return (
      <Card sx={{ height: 350 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top 10 Suppliers
          </Typography>
          <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="textSecondary">No data available</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  // Shorten supplier names for display
  const displayData = data.map((item) => ({
    ...item,
    shortName: item.supplierName.length > 15 ? item.supplierName.slice(0, 12) + '...' : item.supplierName,
  }))

  return (
    <Card sx={{ height: 350 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Top 10 Suppliers by NETTO
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={displayData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`} />
            <YAxis type="category" dataKey="shortName" tick={{ fontSize: 11 }} width={75} />
            <Tooltip
              formatter={(value: number) => [formatCurrency(value), 'NETTO']}
              labelFormatter={(label) => displayData.find((d) => d.shortName === label)?.supplierName || label}
              contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0' }}
            />
            <Bar dataKey="netto" radius={[0, 4, 4, 0]}>
              {displayData.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

interface SupplierDistributionChartProps {
  data: SupplierNettoData[]
  isLoading?: boolean
}

export function SupplierDistributionChart({ data, isLoading }: SupplierDistributionChartProps) {
  // Take top 5 and group rest as "Others"
  const top5 = data.slice(0, 5)
  const others = data.slice(5).reduce((sum, item) => sum + item.netto, 0)

  const chartData =
    others > 0
      ? [...top5, { supplierName: 'Others', netto: others }]
      : data.slice(0, 6)

  if (isLoading) {
    return (
      <Card sx={{ height: 350 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Supplier Distribution
          </Typography>
          <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="textSecondary">Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (chartData.length === 0) {
    return (
      <Card sx={{ height: 350 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Supplier Distribution
          </Typography>
          <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="textSecondary">No data available</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card sx={{ height: 350 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Supplier Distribution (NETTO)
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="netto"
            >
              {chartData.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => formatCurrency(value)} />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
